import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Plus, TrendingUp, Clock, Users, MessageCircle } from "lucide-react";

// Mock data for debates
const mockDebates = [
  {
    id: 1,
    title: "재택근무가 생산성에 도움이 될까?",
    category: "사회",
    agreeCount: 1247,
    disagreeCount: 856,
    totalVotes: 2103,
    commentsCount: 89,
    timeLeft: "2일 남음",
    isHot: true,
    round: 152
  },
  {
    id: 2,
    title: "아이돌 연애는 사생활인가 기만인가?",
    category: "연예",
    agreeCount: 892,
    disagreeCount: 1456,
    totalVotes: 2348,
    commentsCount: 234,
    timeLeft: "1일 남음",
    isHot: true,
    round: 151
  },
  {
    id: 3,
    title: "AI가 인간의 일자리를 대체할까?",
    category: "테크",
    agreeCount: 1678,
    disagreeCount: 743,
    totalVotes: 2421,
    commentsCount: 156,
    timeLeft: "3일 남음",
    isHot: false,
    round: 150
  },
  {
    id: 4,
    title: "탕수육은 찍먹 vs 부먹?",
    category: "일상",
    agreeCount: 2134,
    disagreeCount: 1876,
    totalVotes: 4010,
    commentsCount: 445,
    timeLeft: "5시간 남음",
    isHot: true,
    round: 149
  },
  {
    id: 5,
    title: "대학교 등록금 무료화가 필요할까?",
    category: "정치",
    agreeCount: 1543,
    disagreeCount: 987,
    totalVotes: 2530,
    commentsCount: 178,
    timeLeft: "4일 남음",
    isHot: false,
    round: 148
  },
  {
    id: 6,
    title: "게임은 스포츠인가?",
    category: "게임",
    agreeCount: 1234,
    disagreeCount: 1567,
    totalVotes: 2801,
    commentsCount: 203,
    timeLeft: "6일 남음",
    isHot: false,
    round: 147
  }
];

const categories = ["전체", "일상", "정치", "사회", "연예", "역사", "게임", "테크"];

const Index = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [sortBy, setSortBy] = useState("인기순");

  const filteredDebates = mockDebates.filter(debate => {
    const matchesSearch = debate.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "전체" || debate.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedDebates = [...filteredDebates].sort((a, b) => {
    if (sortBy === "인기순") {
      return b.totalVotes - a.totalVotes;
    } else if (sortBy === "최신순") {
      return b.round - a.round;
    }
    return 0;
  });

  const getAgreePercentage = (agree: number, total: number) => {
    return Math.round((agree / total) * 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold gradient-text">BothSides</span>
              </Link>
              
              <nav className="hidden md:flex space-x-6">
                <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  홈
                </Link>
                <Link to="/create" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  토론 생성
                </Link>
                <Link to="/profile" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  내 프로필
                </Link>
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button asChild variant="outline">
                <Link to="/auth">로그인</Link>
              </Button>
              <Button asChild className="bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600">
                <Link to="/create">
                  <Plus className="w-4 h-4 mr-2" />
                  토론 생성
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">뜨거운 논쟁</span>에 대해
            <br />
            투표하고 토론하기
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            양자택일 투표와 실시간 토론으로 다양한 의견을 나누어보세요
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="토론 주제를 검색해보세요..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-3 text-lg rounded-full border-2 focus:border-blue-500"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? "bg-blue-500 hover:bg-blue-600" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">총 {sortedDebates.length}개의 토론</span>
            </div>
            
            <Tabs value={sortBy} onValueChange={setSortBy} className="w-auto">
              <TabsList>
                <TabsTrigger value="인기순" className="flex items-center space-x-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>인기순</span>
                </TabsTrigger>
                <TabsTrigger value="최신순" className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>최신순</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Debate Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedDebates.map((debate) => {
            const agreePercentage = getAgreePercentage(debate.agreeCount, debate.totalVotes);
            const disagreePercentage = 100 - agreePercentage;
            
            return (
              <Card key={debate.id} className="debate-card group cursor-pointer hover:scale-[1.02]">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="secondary" className="category-tag">
                      {debate.category}
                    </Badge>
                    <div className="flex items-center space-x-2">
                      {debate.isHot && (
                        <Badge variant="destructive" className="bg-red-500">
                          🔥 HOT
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">#{debate.round}</span>
                    </div>
                  </div>
                  
                  <CardTitle className="text-lg leading-tight group-hover:text-blue-600 transition-colors">
                    <Link to={`/debate/${debate.id}`}>
                      {debate.title}
                    </Link>
                  </CardTitle>
                </CardHeader>
                
                <CardContent>
                  {/* Vote Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-600 font-medium">찬성 {agreePercentage}%</span>
                      <span className="text-red-600 font-medium">반대 {disagreePercentage}%</span>
                    </div>
                    
                    <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
                        style={{ width: `${agreePercentage}%` }}
                      />
                      <div 
                        className="absolute right-0 top-0 h-full bg-gradient-to-l from-red-400 to-red-500 transition-all duration-500"
                        style={{ width: `${disagreePercentage}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4" />
                        <span>{debate.totalVotes.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MessageCircle className="w-4 h-4" />
                        <span>{debate.commentsCount}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 text-orange-600">
                      <Clock className="w-4 h-4" />
                      <span>{debate.timeLeft}</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 pt-4 border-t">
                    <Link to={`/debate/${debate.id}`}>
                      <Button className="w-full bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600">
                        참여하기
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {sortedDebates.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto mb-4" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">검색 결과가 없습니다</h3>
            <p className="text-gray-500">다른 키워드로 검색해보세요</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Index;
